import { useEffect, useRef } from 'react';

import { BottomButton } from 'src/utils/widgetSettings';

import './style.scss';

type Props = {
    enabled?: boolean;
    buttons?: BottomButton[];
    /** Sends the button's label as the visitor's message — a button carries no other action. */
    onSelect: (label: string) => void;
};

/**
 * IMBEE-8069 (T86) Area C — the persistent quick-button bar above the message composer.
 *
 * Unlike QuickButtons, which renders buttons attached to a bot message, this strip comes from
 * channel configuration and stays put for the whole conversation. Renders nothing when it is
 * off or when every button is filtered out, so it never leaves an empty bar above the sender.
 */
function BottomButtonBar({ enabled, buttons, onSelect }: Props) {
    // IMBEE-9231 — no filtering here any more. web-chat-services evaluates each button's
    // display condition against the visitor's ticket tags and sends only the survivors, so the
    // tags never reach the browser. This renders the list as given.
    const visible = buttons ?? [];
    const barRef = useRef<HTMLDivElement | null>(null);

    /**
     * A mouse wheel produces vertical deltas, which would scroll the conversation behind the
     * strip rather than the strip itself. Translate them, but only while there is somewhere to
     * go: at either end the event is left alone so the conversation still scrolls normally and
     * the widget never feels stuck.
     */
    /**
     * Attached natively with { passive: false } rather than as an onWheel prop.
     *
     * React 17 registers wheel on the root container as PASSIVE, so preventDefault() from a
     * synthetic handler is a no-op that only logs a warning. The imperative scrollLeft still ran,
     * making the observable result the exact opposite of the intent: the pill strip scrolled
     * sideways AND the conversation scrolled vertically at the same time.
     */
    useEffect(() => {
        const bar = barRef.current;
        if (!bar) return undefined;

        const onWheel = (event: WheelEvent) => {
            if (event.deltaX !== 0) return;
            const maxScroll = bar.scrollWidth - bar.clientWidth;
            if (maxScroll <= 0) return;
            const next = bar.scrollLeft + event.deltaY;
            // At either end the event is left alone, so the conversation scrolls normally and the
            // widget never feels stuck.
            if (next < 0 || next > maxScroll) return;
            bar.scrollLeft = next;
            event.preventDefault();
        };

        bar.addEventListener('wheel', onWheel, { passive: false });
        return () => bar.removeEventListener('wheel', onWheel);
    });

    if (!enabled || visible.length === 0) return null;

    return (
        <div className="bottom-button-bar" data-testid="bottom-button-bar" ref={barRef}>
            <ul className="bottom-button-list">
                {visible.map((button, index) => (
                    <li className="bottom-button-item" key={`${button.label}-${index}`}>
                        <button type="button" onClick={() => onSelect(button.label)}>
                            {button.label}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default BottomButtonBar;
